import { Injectable, computed, inject, signal, untracked, effect } from '@angular/core';
import { BackgroundExecutionService } from '@core/services/background-execution.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { BulkRunHistoryItem, CampaignsService } from '../services/campaigns.service';
import { AnalysisStore } from './analysis-store';
import {
  ACTION_LABELS,
  StageCounts,
  WorkItem,
  WorkflowCountsResponse,
  WorkflowStage,
  WorkflowSummary,
  campaignState,
  nextStep,
} from '../models/campaign-workflow.model';

/** Counts follow a running execution closely; otherwise they refresh slowly. */
const RUN_POLL_MS = 5000;
const IDLE_POLL_MS = 30000;
const HISTORY_SIZE = 100;

export type CampaignTab = 'overview' | 'analyses' | 'executions' | 'review' | 'plannings';

/** Where a tab opens when another tab sends you there. */
export interface CampaignNav {
  /** Analysen: stage in the rail. Ergebnisse prüfen: the queue. */
  stage?: WorkflowStage | 'recheck';
  /** Ergebnisse prüfen: exactly these analyses (Hub ids), in this order. */
  ids?: string[];
  /** Open on this analysis (Hub id). */
  focus?: string;
  view?: 'detection' | 'planning';
  /** Planungsergebnisse: filter in the rail. */
  filter?: string;
  runId?: string;
  at?: number;
}

export interface RunPanelOptions {
  target?: WorkflowStage;
  /** Hub ids of a selection; the panel then skips choosing analyses. */
  ids?: string[];
  /** Address, when it is about one analysis. */
  single?: string | null;
  /** Last step to preselect. */
  to?: number;
  onStart?: () => void;
}

/**
 * One source for a campaign's counts, its running execution and the shared
 * work-item list, plus where the campaign page is showing. Provided by CampaignDetail.
 */
@Injectable()
export class CampaignWorkflowStore {
  private readonly api = inject(CampaignsService);
  private readonly background = inject(BackgroundExecutionService);
  private readonly analyses = inject(AnalysisStore);
  private readonly org = inject(CurrentOrganizationStore);

  readonly organizationId = signal<string | null>(null);
  readonly campaignId = signal<string | null>(null);
  readonly data = signal<WorkflowCountsResponse | null>(null);
  readonly error = signal<string | null>(null);
  readonly cancelling = signal(false);
  readonly items = signal<WorkItem[] | null>(null);
  readonly itemsLoading = signal(false);
  readonly itemsError = signal<string | null>(null);
  readonly runs = signal<BulkRunHistoryItem[]>([]);
  readonly runsLoading = signal(false);
  readonly runsError = signal<string | null>(null);
  /** Per execution: its analyses by stage. Only loaded once a page asks for it. */
  readonly runStages = signal<Record<string, Partial<StageCounts>> | null>(null);
  readonly tab = signal<CampaignTab>('overview');
  readonly nav = signal<CampaignNav>({});
  readonly runPanel = signal<RunPanelOptions | null>(null);
  /** The execution open in its detail panel. */
  readonly runDetailId = signal<string | null>(null);
  /** The review tab asks to finish unsaved edits before anything else opens. */
  leaveGuard: (() => Promise<boolean>) | null = null;

  readonly counts = computed<StageCounts | null>(() => this.data()?.counts ?? null);
  readonly summary = computed<WorkflowSummary | null>(() => this.data()?.summary ?? null);
  readonly total = computed(() => this.data()?.total ?? 0);
  readonly canStart = computed(() => this.org.can('campaign', 'start_processing'));
  readonly activeRun = computed(() => this.data()?.activeRun ?? null);
  readonly running = computed(() => !!this.activeRun());
  readonly next = computed(() => nextStep(this.counts(), this.running()));
  readonly state = computed(() => campaignState(this.counts(), this.running()));
  readonly itemById = computed(() => new Map((this.items() ?? []).map((item) => [item.id, item])));

  readonly run = computed(() => {
    const run = this.activeRun();
    if (!run) return null;
    const total = Math.max(run.total - run.skipped, 0);
    const done = Math.min(run.completed + run.pipelineFailed + run.failed, total);
    return {
      runId: run.runId,
      label: run.action ? ACTION_LABELS[run.action] : 'Berechnung',
      done,
      total,
      errors: run.pipelineFailed + run.failed,
      startedAt: run.startedAt,
    };
  });

  private request = 0;
  private itemsRequest = 0;
  private pollTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    // A run started elsewhere shows up here too.
    effect(() => {
      const id = this.campaignId();
      if (id && this.background.bulkJobs().get(id)?.phase === 'running') untracked(() => void this.refresh());
    });
  }

  init(organizationId: string, campaignId: string): void {
    this.organizationId.set(organizationId);
    this.campaignId.set(campaignId);
    void this.refresh();
    void this.loadRuns();
  }

  destroy(): void {
    clearTimeout(this.pollTimer);
    this.request++;
    this.itemsRequest++;
  }

  async go(tab: CampaignTab, nav: CampaignNav = {}): Promise<void> {
    if (this.leaveGuard && !(await this.leaveGuard())) return;
    this.runPanel.set(null);
    this.runDetailId.set(null);
    this.nav.set({ ...nav, at: Date.now() });
    this.tab.set(tab);
  }

  openRun(options: RunPanelOptions = {}): void {
    if (this.canStart()) this.runPanel.set(options);
  }

  closeRun(): void {
    this.runPanel.set(null);
  }

  /** The run setup hands over to the table; Berechnen there brings it back with the selection. */
  pickFromTable(): void {
    void this.go('analyses');
  }

  /** What the stage's quiet button does: execution always opens its setup first. */
  act(stage: WorkflowStage): void {
    if (stage === 'new' || stage === 'error' || stage === 'replan') this.openRun({ target: stage });
    else if (stage === 'review' || stage === 'seen' || stage === 'flagged') void this.go('review', { stage });
    else if (stage === 'done') void this.go('plannings');
  }

  async refresh(): Promise<void> {
    const org = this.organizationId();
    const campaign = this.campaignId();
    if (!org || !campaign) return;
    const request = ++this.request;
    clearTimeout(this.pollTimer);
    const wasRunning = this.running();
    try {
      const data = await this.api.getWorkflowCounts(org, campaign);
      if (request !== this.request) return;
      const changed = JSON.stringify(data.counts) !== JSON.stringify(this.data()?.counts);
      this.data.set(data);
      this.error.set(null);
      if (changed && this.items()) void this.loadItems();
      if (changed && this.runStages()) void this.loadRunStages();
      if (wasRunning && !data.activeRun) {
        void this.loadRuns();
        void this.analyses.refreshStats();
      }
    } catch {
      if (request === this.request) this.error.set('Der Kampagnenstand konnte nicht geladen werden.');
    } finally {
      if (request === this.request)
        this.pollTimer = setTimeout(() => void this.refresh(), this.running() ? RUN_POLL_MS : IDLE_POLL_MS);
    }
  }

  /** The whole campaign's list, shared by Analysen and Planungsergebnisse; served from the server's snapshot. */
  async loadItems(): Promise<void> {
    const org = this.organizationId();
    const campaign = this.campaignId();
    if (!org || !campaign) return;
    const request = ++this.itemsRequest;
    if (!this.items()) this.itemsLoading.set(true);
    try {
      const { data } = await this.api.getWorkItems(org, campaign, { limit: 50_000 });
      if (request !== this.itemsRequest) return;
      this.items.set(data);
      this.itemsError.set(null);
    } catch {
      if (request === this.itemsRequest) this.itemsError.set('Die Analysen konnten nicht geladen werden.');
    } finally {
      if (request === this.itemsRequest) this.itemsLoading.set(false);
    }
  }

  async loadRunStages(): Promise<void> {
    const org = this.organizationId();
    const campaign = this.campaignId();
    if (!org || !campaign) return;
    try {
      this.runStages.set(await this.api.getRunStages(org, campaign));
    } catch {
      // The table falls back to what the execution itself recorded.
    }
  }

  async loadRuns(): Promise<void> {
    const org = this.organizationId();
    const campaign = this.campaignId();
    if (!org || !campaign) return;
    this.runsLoading.set(true);
    try {
      const { items } = await this.api.listBulkRuns(org, campaign, 1, HISTORY_SIZE);
      this.runs.set(items);
      this.runsError.set(null);
    } catch {
      this.runsError.set('Der Berechnungsverlauf konnte nicht geladen werden.');
    } finally {
      this.runsLoading.set(false);
    }
  }

  /** One analysis changed: keep the list and the counts in step without reloading them. */
  patchItem(item: WorkItem): void {
    const before = this.itemById().get(item.id);
    if (before) this.items.update((items) => items!.map((row) => (row.id === item.id ? item : row)));
    if (!before || before.stage !== item.stage) void this.refresh();
  }

  async cancelRun(): Promise<void> {
    const org = this.organizationId();
    const campaign = this.campaignId();
    const run = this.run();
    if (!org || !campaign || !run) return;
    this.cancelling.set(true);
    try {
      await this.api.cancelRun(org, campaign, run.runId);
      await this.refresh();
      await this.loadRuns();
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Die Berechnung konnte nicht abgebrochen werden.');
    } finally {
      this.cancelling.set(false);
    }
  }
}
